/**
 * Pilot Trends (feat-146) — admin-only trend page for the L&L upsell pilot.
 *
 * Plots four per-(store × day) metric families over the whole benchmark → pilot
 * timeline, grouped test-vs-control, with benchmark/pilot phase bands and a real
 * gap break. Consumes `GET /api/pilot-trends` (ADR-0001 §4). All metric math is
 * backend-computed; this page only reshapes + renders and computes presentation
 * aggregations (7-day rolling mean, group averages) — see pilotTrendsShaping.ts.
 *
 * RBAC: the route is wrapped in <AdminRoute> (App.tsx) — Corporate/Manager are
 * rejected. The endpoint re-enforces admin-only server-side; the 403 state here is
 * presentation only.
 *
 * Design source: worktrees/feat-146-pilot-trends-admin-card/design/DESIGN-NOTES.md
 * + pilot-trends.html (approved 2026-07-07). Charting precedent: feat-096
 * (TrendMetricCriterion.tsx, UpsellDashboard.tsx).
 */
import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  AreaChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  Legend,
} from 'recharts';
import ScorecardNavigation from '../ScorecardNavigation';
import {
  getPilotTrends,
  PilotTrendsForbiddenError,
  PilotTrendsResponse,
  PilotPhase,
  PRE_PERIOD_START,
} from '../../../services/pilotTrendsService';
import {
  reshapeToWide,
  withRolling,
  withIndexed,
  withFit,
  seriesKeys,
  assignStoreColors,
  availableCriteria,
  phaseOf,
  loadTrendPrefs,
  saveTrendPrefs,
  FIT_SUFFIX,
  StoreWithColor,
  SeriesMode,
  SeriesKey,
  TrendPrefs,
  TrendSeriesPrefs,
  TrendStyle,
  BAND,
  REF_COLORS,
  PILOT_COLORS,
  // feat-229 — long-view weekly bucketing
  weeklyBuckets,
  withPhaseSegments,
  withWeeklyFits,
  weeklyPhaseBands,
  sharedDomain,
  sharedIndexedDomain,
  LV,
  SEG,
  LONG_VIEW_BAND_OPACITY,
  WeeklyRow,
  WeeklyFits,
  SharedDomain,
} from './pilotTrendsShaping';

// ── friendly labels for close-rate criteria (fallback: humanized key) ──
const CRIT_LABELS: Record<string, string> = {
  garnish_s_suggest_attach: 'Garnish attach',
  soup_anchor_sandwich_upsell: 'Soup → sandwich',
  salad_anchor_soup_upsell: 'Salad → soup',
  sandwich_anchor_soup_upsell: 'Sandwich → soup',
  drink_promotion: 'Drink promotion',
};
const critLabel = (k: string): string => CRIT_LABELS[k] ?? k.replace(/_/g, ' ');

const monthTick = (d: string): string => {
  const dt = new Date(`${d}T12:00:00`);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const pct = (v: number | null | undefined): string =>
  v == null ? '' : `${Math.round(v * 100)}%`;
// Upsell Score is a /100 metric reported to whole numbers (feat-151 item 4).
const score0 = (v: number): number => Math.round(v);

const gridProps = { strokeDasharray: '3 3', stroke: 'rgba(0,0,0,0.06)', vertical: false } as const;

// ── phase bands + today marker (an ARRAY, spread into the chart) ──
function phaseBands(
  phases: PilotPhase[],
  today: string,
  showLabels: boolean,
  yAxisId?: string,
): React.ReactNode[] {
  const y = yAxisId ? { yAxisId } : {};
  const fill: Record<string, { fill: string; op: number; label?: string; labelFill: string }> = {
    benchmark: { fill: BAND.benchmark, op: 0.07, label: 'BENCHMARK', labelFill: '#B4801F' },
    gap: { fill: BAND.gap, op: 0.1, labelFill: '#717182' },
    pilot: { fill: BAND.pilot, op: 0.05, label: 'PILOT', labelFill: '#B23F30' },
  };
  const bands = phases.map((p) => {
    const f = fill[p.phase] ?? fill.gap;
    return (
      <ReferenceArea
        key={`pb-${p.phase}`}
        {...y}
        x1={p.start}
        x2={p.end}
        fill={f.fill}
        fillOpacity={f.op}
        label={
          showLabels && f.label
            ? { value: f.label, position: 'insideTop', fill: f.labelFill, fontSize: 10, fontWeight: 700 }
            : undefined
        }
      />
    );
  });
  bands.push(
    <ReferenceLine
      key="pb-today"
      {...y}
      x={today}
      stroke={BAND.today}
      strokeDasharray="2 3"
      strokeOpacity={0.5}
    />,
  );
  return bands;
}

function makeTooltip(phases: PilotPhase[], fmt?: (v: number, p?: any) => string) {
  return function TooltipContent({ active, payload, label }: any) {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="rounded-xl bg-ink text-white text-xs shadow-xl px-3 py-2">
        <div className="font-bold mb-1">
          {monthTick(label)} <span className="text-white/50 font-normal">· {phaseOf(label, phases)}</span>
        </div>
        {payload
          // feat-162 AC10 (design-lead ruling): SUPPRESS fitted rows from the
          // tooltip. A regression value is not a real daily observation, and
          // listing it beside real ones invites reading it as data. The legend
          // (`· trend`) is what explains the faint line.
          .filter((p: any) => p.value != null && !String(p.dataKey ?? '').endsWith(FIT_SUFFIX))
          .map((p: any, i: number) => (
            <div key={i} className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color || p.stroke }} />
              <span className="text-white/70">{p.name}</span>
              <span className="ml-auto font-semibold tabular-nums">{fmt ? fmt(p.value, p) : p.value}</span>
            </div>
          ))}
      </div>
    );
  };
}

// Generic SERIES line renderer → array of <Line> (raw daily + optional 7-day
// rolling). NOTE (feat-162): this was called `trendLines()` and did NOT mean
// regression trends — it means "the lines for these series". The regression
// trend renderer is `fitLines()` below; keep the two names distinct.
function seriesLines(
  mode: SeriesMode,
  suffix: string,
  stores: StoreWithColor[],
  showDots: boolean,
  rollingOn: boolean,
  groups: ('test' | 'control')[],
): React.ReactNode[] {
  const keys = seriesKeys(mode, suffix, stores, groups);
  const out: React.ReactNode[] = [];
  keys.forEach(({ key, name, color }) => {
    out.push(
      <Line
        key={key}
        type="monotone"
        dataKey={key}
        name={name}
        stroke={color}
        strokeWidth={rollingOn ? 1 : 2.2}
        strokeOpacity={rollingOn ? 0.3 : 1}
        dot={showDots ? { r: 1.8, fill: color, strokeWidth: 0 } : false}
        activeDot={{ r: 3 }}
        connectNulls={false}
        isAnimationActive={false}
      />,
    );
    if (rollingOn) {
      out.push(
        <Line
          key={`${key}-roll`}
          type="monotone"
          dataKey={`${key}__roll`}
          name={`${name} · 7-day`}
          stroke={color}
          strokeWidth={2.6}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />,
      );
    }
  });
  return out;
}

// ── feat-162: overall trend lines ────────────────────────────────────────────
//
// Visual treatment = design-lead's Question B ruling, in full at
// worktrees/feat-162-…/design/feat-162-trend-treatment.md. The reasoning in one
// line: THE DASH CHANNEL IS SATURATED — "5 3" already means IPT/in-coverage,
// "5 4" benchmark/100%-ref, "6 3" target, "4 2" coverage %. A fifth pattern would
// collide, so the trend is SOLID and subordinates itself on opacity + width
// instead: same store hue, wider than raw (a thin transparent line disappears
// against the gridlines and phase bands; a wide transparent one reads as a soft
// directional underlay), and painted BEFORE its raw line so raw stays the hero.
const TREND_WIDTH_BOOST = 0.6;
const TREND_OPACITY = {
  test: 0.35,
  // Control raw is already drawn at 0.55, so its trend goes fainter still —
  // a trend must never out-shout its own raw series.
  control: 0.3,
  // Chart 2 only: the trend sits over a same-hue Area fill, which eats contrast.
  // This is the one panel where 0.35 is too faint (design-lead's flagged case).
  overFill: 0.5,
} as const;

const CONTROL_HUES: string[] = [PILOT_COLORS.control.a, PILOT_COLORS.control.b];
const isControlHue = (color: string): boolean => CONTROL_HUES.includes(color);

const trendLabel = (style: TrendStyle): string =>
  style === 'linear' ? 'trend' : 'trend (14-day)';

/** Strip the fit suffix so tooltip formatters can key off the underlying metric. */
const baseKey = (dataKey: unknown): string => String(dataKey ?? '').replace(FIT_SUFFIX, '');

/**
 * Regression/trend line renderer → one <Line> per series that HAS a fit.
 *
 * Only keys present in `fittedKeys` are drawn — a series with <2 non-null points
 * yields no fit and therefore no line (never a flat line through one dot).
 *
 * ⚠ CALL SITES MUST render this BEFORE the matching raw <Line> — SVG paint order
 * is what keeps the crisp raw series on top and primary (design-lead's ruling).
 * The one exception is Chart 2, where the trend paints above the Area fills.
 *
 * ⚠ Dual-axis: callers in the Ring & IPT absolute block MUST pass the matching
 * `yAxisId` ('ring' | 'ipt'). Omitting it silently plots IPT against the dollar
 * axis. The indexed lens has a single default axis and takes NO yAxisId.
 *
 * `rawWidth` is the width of the series' own raw line; the trend is drawn
 * `TREND_WIDTH_BOOST` wider than it.
 */
function fitLines(
  keys: SeriesKey[],
  fittedKeys: string[],
  style: TrendStyle,
  opts: { yAxisId?: string; rawWidth: number; opacity?: number },
): React.ReactNode[] {
  const y = opts.yAxisId ? { yAxisId: opts.yAxisId } : {};
  return keys
    .filter((k) => fittedKeys.includes(k.key))
    .map((k) => (
      <Line
        key={`${k.key}-fit`}
        {...y}
        // A least-squares fit IS a straight line: `linear` guarantees it renders
        // as one. (Visually identical to the ruling's `monotone` — monotone
        // interpolation of collinear points is a straight line — but it can't
        // wobble on the 3-decimal rounding in `linearFit`.) The 14-day rolling
        // style is a real curve, so it keeps `monotone`.
        type={style === 'linear' ? 'linear' : 'monotone'}
        dataKey={`${k.key}${FIT_SUFFIX}`}
        name={`${k.name} · ${trendLabel(style)}`}
        stroke={k.color}
        strokeWidth={opts.rawWidth + TREND_WIDTH_BOOST}
        strokeOpacity={opts.opacity ?? (isControlHue(k.color) ? TREND_OPACITY.control : TREND_OPACITY.test)}
        // NO strokeDasharray — solid is the only non-colliding pattern.
        dot={false}
        activeDot={false}
        connectNulls
        isAnimationActive={false}
      />
    ));
}

/** Per-block trend series toggle (Decision 4), disabled when TREND is globally off. */
const TrendCheck: React.FC<{
  label: string;
  on: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, on, disabled, onChange }) => (
  <label
    className={`flex items-center gap-1.5 text-xs font-semibold select-none ${
      disabled ? 'text-inkMuted/40 cursor-not-allowed' : 'text-inkMuted cursor-pointer'
    }`}
    title={disabled ? 'Turn TREND on in the control bar to use this' : undefined}
  >
    <input
      type="checkbox"
      checked={on && !disabled}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="accent-coral"
    />
    {label}
  </label>
);

const commonX = { dataKey: 'date', tickFormatter: monthTick, interval: 6, tickMargin: 6, minTickGap: 24 } as const;

// annotation for the test-only charts (1/3/4).
const BaselineNote: React.FC = () => (
  <div className="mt-2 inline-flex items-center gap-2 text-[11px] font-semibold text-inkMuted bg-cream-dark rounded-full px-3 py-1">
    <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-dashed border-inkMuted/50" />
    Control stores: baseline only (no app recording) — test lines only here. Head-to-head lives in chart 2.
  </div>
);

const ChartCard: React.FC<{
  n: string;
  title: string;
  sub: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ n, title, sub, right, children }) => (
  <section className="bg-white rounded-3xl border border-softBorder shadow-sm p-5 mb-5">
    <div className="flex items-start justify-between gap-4 mb-3">
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-coral-light text-coral font-display font-bold text-sm flex items-center justify-center mt-0.5">
          {n}
        </span>
        <div>
          <h3 className="font-display font-semibold text-lg leading-tight">{title}</h3>
          <p className="text-sm text-inkMuted">{sub}</p>
        </div>
      </div>
      {right}
    </div>
    {children}
  </section>
);

interface ChartProps {
  resp: PilotTrendsResponse;
  stores: StoreWithColor[];
  today: string;
  mode: SeriesMode;
  showDots: boolean;
  rollingOn: boolean;
  /** feat-162 trend preferences (global on/off + style + per-series + per-lens). */
  trend: TrendPrefs;
  /** Flip one block's series trend on/off; persisted by the parent. */
  setSeries: (id: keyof TrendSeriesPrefs, on: boolean) => void;
  /** Flip the Ring & IPT per-lens trend gate (Decision 6). */
  setLensTrend: (lens: RiLens, on: boolean) => void;
  /** Flip a lens-less block's own trend gate (feat-179 Revenue & Transactions). */
  setBlockTrend: (block: keyof TrendPrefs['blocks'], on: boolean) => void;
}

// Chart 1 — Upsell Score + benchmark/target reference lines + PP/Attachment split
const Chart1: React.FC<ChartProps> = ({ resp, stores, today, mode, showDots, rollingOn, trend, setSeries }) => {
  const [split, setSplit] = React.useState(false);
  const test = stores.filter((s) => s.group === 'test');
  const base = React.useMemo(() => reshapeToWide(resp), [resp]);
  const keys = React.useMemo(() => seriesKeys(mode, '', stores, ['test']), [mode, stores]);
  const trendOn = trend.on && trend.series.upsell;
  const { rows: data, fittedKeys } = React.useMemo(() => {
    const rolled = withRolling(base, keys.map((k) => k.key));
    // Fit the RAW daily series, not the 7-day-smoothed one — the trend is over
    // observations, independent of the SMOOTHING control.
    return withFit(rolled, trendOn ? keys.map((k) => k.key) : [], trend.style);
  }, [base, keys, trendOn, trend.style]);

  const refLines: React.ReactNode[] = [];
  if (mode === 'store') {
    test.forEach((s) => {
      if (s.benchmark_upsell_score != null)
        refLines.push(
          <ReferenceLine
            key={`${s.location_id}-b`}
            y={s.benchmark_upsell_score}
            stroke={s.color}
            strokeDasharray="5 4"
            strokeOpacity={0.4}
            label={{ value: `${s.name} benchmark ${score0(s.benchmark_upsell_score)}`, position: 'right', fill: s.color, fontSize: 9, fillOpacity: 0.85 }}
          />,
        );
      if (s.target_upsell_score != null)
        refLines.push(
          <ReferenceLine
            key={`${s.location_id}-t`}
            y={s.target_upsell_score}
            stroke={REF_COLORS.target}
            strokeDasharray="6 3"
            label={{ value: `target ${score0(s.target_upsell_score)}`, position: 'right', fill: REF_COLORS.targetLabel, fontSize: 9 }}
          />,
        );
    });
  } else {
    const benchVals = test.map((s) => s.benchmark_upsell_score).filter((v): v is number => v != null);
    const targetVals = test.map((s) => s.target_upsell_score).filter((v): v is number => v != null);
    if (benchVals.length) {
      const bAvg = score0(benchVals.reduce((a, b) => a + b, 0) / benchVals.length);
      refLines.push(
        <ReferenceLine key="b" y={bAvg} stroke={REF_COLORS.benchmark} strokeDasharray="5 4" label={{ value: `test benchmark avg ${bAvg}`, position: 'insideBottomRight', fill: REF_COLORS.benchmark, fontSize: 9 }} />,
      );
    }
    if (targetVals.length) {
      const tAvg = score0(targetVals.reduce((a, b) => a + b, 0) / targetVals.length);
      refLines.push(
        <ReferenceLine key="t" y={tAvg} stroke={REF_COLORS.target} strokeDasharray="6 3" label={{ value: `test target avg ${tAvg}`, position: 'insideTopRight', fill: REF_COLORS.targetLabel, fontSize: 9, fontWeight: 700 }} />,
      );
    }
  }

  return (
    <ChartCard
      n="1"
      title="Upsell Score /100"
      sub="(total upsell attempts ÷ T) × 100 — coverage-invariant, the headline metric. Test stores carry benchmark + target (bogey) reference lines."
      right={
        <div className="flex items-center gap-4 flex-wrap justify-end">
          <TrendCheck
            label="Trend: Upsell Score"
            on={trend.series.upsell}
            disabled={!trend.on}
            onChange={(v) => setSeries('upsell', v)}
          />
          <label className="flex items-center gap-2 text-xs font-semibold text-inkMuted cursor-pointer select-none">
            <input type="checkbox" checked={split} onChange={(e) => setSplit(e.target.checked)} className="accent-coral" /> Show PP / Attachment split
          </label>
        </div>
      }
    >
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 18, right: 96, left: 0, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            {phaseBands(resp.phases, today, true)}
            <XAxis {...commonX} />
            <YAxis width={44} tick={{ fontSize: 11 }} domain={[0, 'auto']} />
            <Tooltip content={makeTooltip(resp.phases, (v) => String(score0(v)))} />
            {refLines}
            {/* trend first → raw paints on top and stays the primary read */}
            {fitLines(keys, fittedKeys, trend.style, { rawWidth: 2.2 })}
            {seriesLines(mode, '', stores, showDots, rollingOn, ['test'])}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <BaselineNote />
      {split && (
        <div className="mt-2 border-t border-softBorder pt-3">
          <p className="text-xs font-semibold text-inkMuted mb-1">Composition — Perfect-Pairing vs Attachment (test group avg)</p>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={base} margin={{ top: 6, right: 96, left: 0, bottom: 0 }}>
                <CartesianGrid {...gridProps} />
                {phaseBands(resp.phases, today, false)}
                <XAxis {...commonX} />
                <YAxis width={44} tick={{ fontSize: 11 }} />
                <Tooltip content={makeTooltip(resp.phases, (v) => String(score0(v)))} />
                <Area type="monotone" stackId="pp" dataKey="testAvg_pp" name="Perfect-Pairing" stroke={PILOT_COLORS.test.a} fill={PILOT_COLORS.test.a} fillOpacity={0.55} connectNulls={false} isAnimationActive={false} />
                <Area type="monotone" stackId="pp" dataKey="testAvg_at" name="Attachment" stroke={PILOT_COLORS.test.b} fill={PILOT_COLORS.test.b} fillOpacity={0.28} connectNulls={false} isAnimationActive={false} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </ChartCard>
  );
};

// Chart 2 — Transactions total volume (halo) — the head-to-head, BOTH groups
const Chart2: React.FC<ChartProps> = ({ resp, stores, today, mode, trend, setSeries }) => {
  const raw = React.useMemo(() => reshapeToWide(resp), [resp]);
  const test = stores.filter((s) => s.group === 'test');

  // Trend on total volume (the primary series, spec Question C) — both cohorts,
  // since total volume is recording-independent.
  const totalKeys = React.useMemo(() => seriesKeys(mode, '_total', stores), [mode, stores]);
  const trendOn = trend.on && trend.series.transactions;
  const { rows: base, fittedKeys } = React.useMemo(
    () => withFit(raw, trendOn ? totalKeys.map((k) => k.key) : [], trend.style),
    [raw, totalKeys, trendOn, trend.style],
  );

  const storeLines: React.ReactNode[] = [];
  stores.forEach((s) =>
    storeLines.push(
      <Line
        key={`${s.location_id}-tot`}
        type="monotone"
        dataKey={`${s.location_id}_total`}
        name={`${s.name}${s.group === 'control' ? ' · total (baseline)' : ' · total'}`}
        stroke={s.color}
        strokeWidth={2.2}
        dot={false}
        connectNulls={false}
        isAnimationActive={false}
      />,
    ),
  );
  test.forEach((s) =>
    storeLines.push(
      <Line
        key={`${s.location_id}-cov`}
        type="monotone"
        dataKey={`${s.location_id}_incov`}
        name={`${s.name} · in mic coverage`}
        stroke={s.color}
        strokeWidth={1.6}
        strokeDasharray="5 3"
        dot={false}
        connectNulls={false}
        isAnimationActive={false}
      />,
    ),
  );

  return (
    <ChartCard
      n="2"
      title="Transactions — total volume (halo)"
      sub="Total daily orders — recording-independent, so BOTH groups get a continuous line. This is the head-to-head: did test's total volume diverge from the control baseline? Dashed = test's mic-covered subset (the T denominator)."
      right={
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <TrendCheck
            label="Trend: Transactions"
            on={trend.series.transactions}
            disabled={!trend.on}
            onChange={(v) => setSeries('transactions', v)}
          />
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-coral bg-coral-light rounded-full px-3 py-1">★ TEST vs CONTROL head-to-head</span>
        </div>
      }
    >
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          {mode === 'store' ? (
            <ComposedChart data={base} margin={{ top: 18, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              {phaseBands(resp.phases, today, true)}
              <XAxis {...commonX} />
              <YAxis width={44} tick={{ fontSize: 11 }} />
              <Tooltip content={makeTooltip(resp.phases)} />
              {/* store mode is lines-only (no fills) → standard treatment, trend under raw */}
              {fitLines(totalKeys, fittedKeys, trend.style, { rawWidth: 2.2 })}
              {storeLines}
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </ComposedChart>
          ) : (
            <ComposedChart data={base} margin={{ top: 18, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              {phaseBands(resp.phases, today, true)}
              <XAxis {...commonX} />
              <YAxis width={44} tick={{ fontSize: 11 }} />
              <Tooltip content={makeTooltip(resp.phases)} />
              <Area type="monotone" dataKey="testAvg_total" name="Test · total orders" stroke={PILOT_COLORS.test.a} fill={PILOT_COLORS.test.a} fillOpacity={0.12} strokeWidth={2.4} connectNulls={false} isAnimationActive={false} />
              <Area type="monotone" dataKey="ctrlAvg_total" name="Control · total orders (baseline)" stroke={PILOT_COLORS.control.a} fill={PILOT_COLORS.control.a} fillOpacity={0.1} strokeWidth={2.4} connectNulls={false} isAnimationActive={false} />
              {/* ⚠ The density edge (design-lead's flagged case): this panel's
                  trends sit over a SAME-HUE Area fill, so unlike everywhere else
                  they paint ABOVE the fill and at a higher opacity — 0.35 under a
                  fill is unreadable. The dashed in-coverage line still lands on
                  top of them. */}
              {fitLines(totalKeys, fittedKeys, trend.style, { rawWidth: 2.4, opacity: TREND_OPACITY.overFill })}
              <Line type="monotone" dataKey="testAvg_incov" name="Test · in mic coverage" stroke={PILOT_COLORS.test.a} strokeWidth={2} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-inkMuted mt-2">
        Halo read: controls never record, so they appear as total POS volume only (the baseline). If test's total pulls above control's over the pilot — beyond just the mic-covered subset — that divergence is the halo effect.
        {mode === 'store' && <span> Per-store view is lines-only for legibility; if the series read too dense we can fall back to 2×2 small multiples.</span>}
      </p>
    </ChartCard>
  );
};

// Chart 3 — order-grain close rate, per-criterion drill-down (test-only)
const Chart3: React.FC<ChartProps> = ({ resp, stores, today, mode, showDots, rollingOn, trend, setSeries }) => {
  const [crit, setCrit] = React.useState('all');
  const criteria = React.useMemo(() => availableCriteria(resp), [resp]);
  const base = React.useMemo(() => reshapeToWide(resp, crit), [resp, crit]);
  const keys = React.useMemo(() => seriesKeys(mode, '_close', stores, ['test']), [mode, stores]);
  const trendOn = trend.on && trend.series.close;
  const { rows: data, fittedKeys } = React.useMemo(() => {
    const rolled = withRolling(base, keys.map((k) => k.key));
    return withFit(rolled, trendOn ? keys.map((k) => k.key) : [], trend.style);
  }, [base, keys, trendOn, trend.style]);

  return (
    <ChartCard
      n="3"
      title="Close rate (order-grain)"
      sub="match ÷ linked-orders — of guests we offered and could trace to a check, what share bought."
      right={
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <TrendCheck
            label="Trend: Close rate"
            on={trend.series.close}
            disabled={!trend.on}
            onChange={(v) => setSeries('close', v)}
          />
          <select value={crit} onChange={(e) => setCrit(e.target.value)} className="text-xs font-semibold text-ink bg-cream border border-softBorder rounded-full px-3 py-1.5">
            <option value="all">All criteria</option>
            {criteria.map((c) => (
              <option key={c} value={c}>
                {critLabel(c)}
              </option>
            ))}
          </select>
        </div>
      }
    >
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 18, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            {phaseBands(resp.phases, today, true)}
            <XAxis {...commonX} />
            <YAxis width={44} tick={{ fontSize: 11 }} tickFormatter={pct} domain={[0, 0.8]} />
            <Tooltip content={makeTooltip(resp.phases, (v) => pct(v))} />
            {/* trend first → raw paints on top */}
            {fitLines(keys, fittedKeys, trend.style, { rawWidth: 2.2 })}
            {seriesLines(mode, '_close', stores, showDots, rollingOn, ['test'])}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <BaselineNote />
      <p className="text-xs text-inkMuted mt-2">
        ⚠ Early / low-volume pilot days are noisy — the 7-day rolling line is the trend to read; single daily dots can swing hard.
        {crit !== 'all' && <span className="text-ink font-semibold"> Showing “{critLabel(crit)}” only.</span>}
      </p>
    </ChartCard>
  );
};

// Chart 4 — recorded attempts/day + coverage % (dual axis, test-only)
const Chart4: React.FC<ChartProps> = ({ resp, stores, today, mode, trend, setSeries }) => {
  const raw = React.useMemo(() => reshapeToWide(resp), [resp]);
  const test = stores.filter((s) => s.group === 'test');

  // Attempts = the primary series (default ON); coverage % = companion (opt-in).
  // ⚠ Dual axis: attempts live on 'att', coverage on 'cov' — each trend <Line>
  // must carry its own axis or coverage (0–1) gets plotted against attempts/day.
  const attKeys = React.useMemo(() => seriesKeys(mode, '_att', stores, ['test']), [mode, stores]);
  const covKeys = React.useMemo(() => seriesKeys(mode, '_cov', stores, ['test']), [mode, stores]);
  const attTrend = trend.on && trend.series.attempts;
  const covTrend = trend.on && trend.series.coverage;
  const { rows: base, fittedKeys } = React.useMemo(
    () =>
      withFit(
        raw,
        [
          ...(attTrend ? attKeys.map((k) => k.key) : []),
          ...(covTrend ? covKeys.map((k) => k.key) : []),
        ],
        trend.style,
      ),
    [raw, attKeys, covKeys, attTrend, covTrend, trend.style],
  );

  return (
    <ChartCard
      n="4"
      title="Recorded attempts / day + coverage %"
      sub="The operational input side: are they recording (coverage % = T ÷ all orders), and are they offering (attempts/day)?"
      right={
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <TrendCheck
            label="Trend: Attempts"
            on={trend.series.attempts}
            disabled={!trend.on}
            onChange={(v) => setSeries('attempts', v)}
          />
          <TrendCheck
            label="Trend: Coverage %"
            on={trend.series.coverage}
            disabled={!trend.on}
            onChange={(v) => setSeries('coverage', v)}
          />
        </div>
      }
    >
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={base} margin={{ top: 18, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            {phaseBands(resp.phases, today, true, 'att')}
            <XAxis {...commonX} />
            <YAxis yAxisId="att" width={44} tick={{ fontSize: 11 }} label={{ value: 'attempts/day', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#717182', dy: 40 }} />
            <YAxis yAxisId="cov" orientation="right" width={40} tick={{ fontSize: 11 }} tickFormatter={pct} domain={[0, 1]} />
            {/* baseKey() strips `__fit` so a coverage TREND still formats as a %. */}
            <Tooltip content={makeTooltip(resp.phases, (v, p) => (baseKey(p?.dataKey).endsWith('_cov') ? `${(v * 100).toFixed(2)}%` : String(v)))} />
            {mode === 'store'
              ? test.map((s) => (
                  <Bar key={`${s.location_id}-att`} yAxisId="att" dataKey={`${s.location_id}_att`} name={`${s.name} · attempts/day`} fill={s.color} fillOpacity={0.8} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                ))
              : (
                <Bar yAxisId="att" dataKey="testAvg_att" name="Test · attempts/day" fill={PILOT_COLORS.test.a} fillOpacity={0.75} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              )}
            {mode === 'store'
              ? test.map((s) => (
                  <Line key={`${s.location_id}-cov`} yAxisId="cov" type="monotone" dataKey={`${s.location_id}_cov`} name={`${s.name} · coverage %`} stroke={s.color} strokeWidth={2.2} strokeDasharray="4 2" dot={false} connectNulls={false} isAnimationActive={false} />
                ))
              : (
                <Line yAxisId="cov" type="monotone" dataKey="testAvg_cov" name="Test · coverage %" stroke={PILOT_COLORS.test.a} strokeWidth={2.4} strokeDasharray="4 2" dot={false} connectNulls={false} isAnimationActive={false} />
              )}
            {/* Attempts renders as BARS, so its trend is a line over bars — no
                line-on-line competition (design-lead: the cleanest trend on the
                page). Coverage's raw is the "4 2" dashed line. */}
            {fitLines(attKeys, fittedKeys, trend.style, { yAxisId: 'att', rawWidth: 2.2 })}
            {fitLines(covKeys, fittedKeys, trend.style, { yAxisId: 'cov', rawWidth: mode === 'store' ? 2.2 : 2.4 })}
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <BaselineNote />
      <p className="text-xs text-inkMuted mt-2">
        Bars = attempts/day (left axis). Dashed = coverage % (right axis). This is the “are they doing the work?” view the coverage-invariant score deliberately abstracts away — test stores only (controls don't record). “All 4 stores” breaks it out per test store.
      </p>
    </ChartCard>
  );
};

// ── Ring (AOV) + IPT — the effectiveness headline (feat-152 item 2) ──
// Ring/IPT are POS-order aggregates (coverage-INDEPENDENT) → unlike Charts 1/3/4
// the CONTROL stores get a real baseline too (spec AC4). Two lenses (design brief
// design/feat-152-brief.md, approved by Preston 2026-07-10):
//   • Absolute — per-store dual-axis small-multiples (store mode) or a single
//     test-vs-control dual-axis chart (group mode). Ring = solid ($ left axis),
//     IPT = dashed (count right axis), same store hue (stroke, not a 2nd hue,
//     differentiates the metric). domain=['auto','auto'] on BOTH axes — a 0-floor
//     compresses the $18–26 band and kills the divergence read (design-lead note).
//   • % of benchmark — each metric indexed to its own benchmark-window avg = 100%
//     so Ring + IPT share ONE honest axis; the cleanest proof of the
//     upsell→IPT→Ring thesis (test climbs together, control stays flat).
// Reuses the global Grouping + Smoothing toggles; adds a local Lens toggle.
// ⚠ Recharts dual-axis gotcha: the phase-band ReferenceArea/ReferenceLine MUST
// carry a yAxisId in the dual-axis charts or Recharts throws — hence
// phaseBands(..., 'ring'). The indexed lens has a single default axis (no yAxisId).

// Per-metric tooltip formatter: indexed → `XX%`, Ring → `$X.XX`, IPT → `X.XX`.
// Keyed off the dataKey suffix (the `_idx` check MUST precede `_ring`, since an
// indexed Ring key like `..._ring_idx` also contains `_ring`).
const riFmt = (v: number, p?: any): string => {
  const dk = String(p?.dataKey ?? '');
  if (dk.includes('_idx')) return `${Math.round(v)}%`;
  if (dk.includes('_ring')) return `$${v.toFixed(2)}`;
  return v.toFixed(2);
};

type RiLens = 'absolute' | 'indexed';
const RI_GROUP_KEYS = ['testAvg_ring', 'testAvg_ipt', 'ctrlAvg_ring', 'ctrlAvg_ipt'] as const;

const RingIptChart: React.FC<ChartProps> = ({ resp, stores, today, mode, rollingOn, trend, setSeries, setLensTrend }) => {
  // Default lens = Absolute ($ / count) per Preston's ruling (2026-07-10): over a
  // short pilot window the %-of-benchmark index adds little, so open on the real
  // $/count view. Both lenses still ship via the Lens toggle (Lens B = one click).
  const [lens, setLens] = React.useState<RiLens>('absolute');

  const hasData = React.useMemo(
    () => resp.series.some((r) => r.ring != null || r.ipt != null),
    [resp],
  );

  const base = React.useMemo(() => reshapeToWide(resp), [resp]);
  const allKeys = React.useMemo(
    () => [...stores.flatMap((s) => [s.location_id + '_ring', s.location_id + '_ipt']), ...RI_GROUP_KEYS],
    [stores],
  );
  // feat-162 — trend gating for this block. Decision 6: the per-LENS gate defaults
  // ON in Absolute and OFF in % of benchmark (the 100% reference line already
  // carries a reading there). Decision 9: Ring is the primary (ON), IPT opt-in.
  const lensTrend = trend.on && trend.lens[lens];
  const wantRing = lensTrend && trend.series.ring;
  const wantIpt = lensTrend && trend.series.ipt;

  // ⚠ In the % lens we fit the `_idx` keys — the indexed series the axis actually
  // plots — NOT an absolute fit rescaled after the fact. The two are different
  // lines, and the drawn line must match the number on the axis.
  const suffix = lens === 'indexed' ? '_idx' : '';
  const fitTargets = React.useMemo(
    () =>
      allKeys
        .filter((k) => (k.endsWith('_ring') ? wantRing : wantIpt))
        .map((k) => k + suffix),
    [allKeys, wantRing, wantIpt, suffix],
  );

  const { rows: data, fittedKeys } = React.useMemo(() => {
    const idx = withIndexed(base, allKeys);
    const rolled = rollingOn
      ? withRolling(idx, [...allKeys, ...allKeys.map((k) => `${k}_idx`)])
      : idx;
    // Fit the raw/indexed daily series, never the 7-day-smoothed one.
    return withFit(rolled, fitTargets, trend.style);
  }, [base, allKeys, rollingOn, fitTargets, trend.style]);

  const rk = (key: string): string => (rollingOn ? `${key}__roll` : key);

  /** SeriesKey for a store/cohort metric on the CURRENT lens (adds `_idx` in %). */
  const sk = (key: string, name: string, color: string): SeriesKey[] => [
    { key: key + suffix, name, color },
  ];

  const headerRight = (
    <div className="flex items-center gap-3 flex-wrap justify-end">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-coral bg-coral-light rounded-full px-3 py-1">★ EFFECTIVENESS</span>
      <div className="inline-flex items-center gap-1.5">
        <span className="text-xs font-bold uppercase tracking-wide text-inkMuted">Lens</span>
        <div className="inline-flex rounded-full border border-softBorder bg-white p-0.5">
          <button onClick={() => setLens('absolute')} className={`rounded-full px-3 py-1 text-[13px] font-bold ${lens === 'absolute' ? 'bg-coral text-white' : 'text-inkMuted'}`}>
            Absolute ($ / count)
          </button>
          <button onClick={() => setLens('indexed')} className={`rounded-full px-3 py-1 text-[13px] font-bold ${lens === 'indexed' ? 'bg-coral text-white' : 'text-inkMuted'}`}>
            % of benchmark
          </button>
        </div>
      </div>
      {/* feat-162 per-block series toggles + the per-lens gate (Decision 4 / 6).
          The lens checkbox is remembered per lens, so turning trends on in % once
          keeps them on in % — the default is a default, not a ban. */}
      <div className="inline-flex items-center gap-3 flex-wrap">
        <TrendCheck
          label={`Trend in ${lens === 'absolute' ? 'Absolute' : '%'}`}
          on={trend.lens[lens]}
          disabled={!trend.on}
          onChange={(v) => setLensTrend(lens, v)}
        />
        <TrendCheck
          label="Ring"
          on={trend.series.ring}
          disabled={!lensTrend}
          onChange={(v) => setSeries('ring', v)}
        />
        <TrendCheck
          label="IPT"
          on={trend.series.ipt}
          disabled={!lensTrend}
          onChange={(v) => setSeries('ipt', v)}
        />
      </div>
    </div>
  );

  // ── per-store small-multiple panels (2×2 grid) ──
  const panelHeader = (s: StoreWithColor) => (
    <div className="flex items-center gap-2 mb-1">
      <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color }} />
      <span className="font-semibold text-sm truncate">{s.name}</span>
      <span className="text-[10px] text-inkMuted flex-shrink-0">({s.group})</span>
    </div>
  );

  const absPanel = (s: StoreWithColor) => (
    <div key={s.location_id} className="bg-white rounded-2xl border border-softBorder p-3">
      {panelHeader(s)}
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            {phaseBands(resp.phases, today, false, 'ring')}
            <XAxis {...commonX} />
            <YAxis yAxisId="ring" width={40} tick={{ fontSize: 9 }} tickFormatter={(v) => `$${v}`} domain={['auto', 'auto']} />
            <YAxis yAxisId="ipt" orientation="right" width={30} tick={{ fontSize: 9 }} domain={['auto', 'auto']} />
            <Tooltip content={makeTooltip(resp.phases, riFmt)} />
            {/* ⚠ dual axis — Ring trend on 'ring' ($), IPT trend on 'ipt' (count).
                Painted BEFORE the raw lines so raw stays on top. */}
            {fitLines(sk(`${s.location_id}_ring`, `${s.name} · Ring (AOV)`, s.color), fittedKeys, trend.style, { yAxisId: 'ring', rawWidth: 2.4 })}
            {fitLines(sk(`${s.location_id}_ipt`, `${s.name} · IPT`, s.color), fittedKeys, trend.style, { yAxisId: 'ipt', rawWidth: 1.8 })}
            <Line yAxisId="ring" type="monotone" dataKey={rk(`${s.location_id}_ring`)} name={`${s.name} · Ring (AOV)`} stroke={s.color} strokeWidth={2.4} dot={false} connectNulls={false} isAnimationActive={false} />
            <Line yAxisId="ipt" type="monotone" dataKey={rk(`${s.location_id}_ipt`)} name={`${s.name} · IPT`} stroke={s.color} strokeWidth={1.8} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const idxPanel = (s: StoreWithColor) => (
    <div key={s.location_id} className="bg-white rounded-2xl border border-softBorder p-3">
      {panelHeader(s)}
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            {phaseBands(resp.phases, today, false)}
            <XAxis {...commonX} />
            <YAxis width={40} tick={{ fontSize: 9 }} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
            <ReferenceLine y={100} stroke={REF_COLORS.benchmark} strokeDasharray="5 4" />
            <Tooltip content={makeTooltip(resp.phases, riFmt)} />
            {/* single default axis here — NO yAxisId (see the block comment above). */}
            {fitLines(sk(`${s.location_id}_ring`, `${s.name} · Ring (AOV)`, s.color), fittedKeys, trend.style, { rawWidth: 2.4 })}
            {fitLines(sk(`${s.location_id}_ipt`, `${s.name} · IPT`, s.color), fittedKeys, trend.style, { rawWidth: 1.8 })}
            <Line type="monotone" dataKey={rk(`${s.location_id}_ring_idx`)} name={`${s.name} · Ring (AOV)`} stroke={s.color} strokeWidth={2.4} dot={false} connectNulls={false} isAnimationActive={false} />
            <Line type="monotone" dataKey={rk(`${s.location_id}_ipt_idx`)} name={`${s.name} · IPT`} stroke={s.color} strokeWidth={1.8} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  // ── group charts (single wide chart) ──
  const groupAbsolute = (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          {phaseBands(resp.phases, today, true, 'ring')}
          <XAxis {...commonX} />
          <YAxis yAxisId="ring" width={46} tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} domain={['auto', 'auto']} label={{ value: 'Ring (AOV)', angle: -90, position: 'insideLeft', fontSize: 10, fill: REF_COLORS.benchmark, dy: 30 }} />
          <YAxis yAxisId="ipt" orientation="right" width={40} tick={{ fontSize: 11 }} domain={['auto', 'auto']} label={{ value: 'IPT', angle: 90, position: 'insideRight', fontSize: 10, fill: REF_COLORS.benchmark }} />
          <Tooltip content={makeTooltip(resp.phases, riFmt)} />
          {/* ⚠ dual axis — Ring trends on 'ring' ($), IPT trends on 'ipt' (count).
              Painted BEFORE the raw lines. Control trends self-dim to 0.30 (their
              raw is already 0.55) via the hue check in fitLines. */}
          {fitLines(sk('testAvg_ring', 'Test · Ring (AOV)', PILOT_COLORS.test.a), fittedKeys, trend.style, { yAxisId: 'ring', rawWidth: 2.6 })}
          {fitLines(sk('ctrlAvg_ring', 'Control · Ring (baseline)', PILOT_COLORS.control.a), fittedKeys, trend.style, { yAxisId: 'ring', rawWidth: 2 })}
          {fitLines(sk('testAvg_ipt', 'Test · IPT', PILOT_COLORS.test.a), fittedKeys, trend.style, { yAxisId: 'ipt', rawWidth: 1.8 })}
          {fitLines(sk('ctrlAvg_ipt', 'Control · IPT (baseline)', PILOT_COLORS.control.a), fittedKeys, trend.style, { yAxisId: 'ipt', rawWidth: 1.5 })}
          <Line yAxisId="ring" type="monotone" dataKey={rk('testAvg_ring')} name="Test · Ring (AOV)" stroke={PILOT_COLORS.test.a} strokeWidth={2.6} dot={false} connectNulls={false} isAnimationActive={false} />
          <Line yAxisId="ipt" type="monotone" dataKey={rk('testAvg_ipt')} name="Test · IPT" stroke={PILOT_COLORS.test.a} strokeWidth={1.8} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
          <Line yAxisId="ring" type="monotone" dataKey={rk('ctrlAvg_ring')} name="Control · Ring (baseline)" stroke={PILOT_COLORS.control.a} strokeWidth={2} strokeOpacity={0.55} dot={false} connectNulls={false} isAnimationActive={false} />
          <Line yAxisId="ipt" type="monotone" dataKey={rk('ctrlAvg_ipt')} name="Control · IPT (baseline)" stroke={PILOT_COLORS.control.a} strokeWidth={1.5} strokeOpacity={0.55} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );

  const groupIndexed = (
    <div className="h-96">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          {phaseBands(resp.phases, today, true)}
          <XAxis {...commonX} />
          <YAxis width={48} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={['auto', 'auto']} />
          <ReferenceLine y={100} stroke={REF_COLORS.benchmark} strokeDasharray="5 4" label={{ value: 'benchmark = 100%', position: 'insideBottomRight', fontSize: 9, fill: REF_COLORS.benchmark }} />
          <Tooltip content={makeTooltip(resp.phases, riFmt)} />
          {/* single default axis here — NO yAxisId. Fitted on the `_idx` series. */}
          {fitLines(sk('testAvg_ring', 'Test · Ring (AOV)', PILOT_COLORS.test.a), fittedKeys, trend.style, { rawWidth: 2.4 })}
          {fitLines(sk('ctrlAvg_ring', 'Control · Ring (AOV)', PILOT_COLORS.control.a), fittedKeys, trend.style, { rawWidth: 2.2 })}
          {fitLines(sk('testAvg_ipt', 'Test · IPT', PILOT_COLORS.test.a), fittedKeys, trend.style, { rawWidth: 1.8 })}
          {fitLines(sk('ctrlAvg_ipt', 'Control · IPT', PILOT_COLORS.control.a), fittedKeys, trend.style, { rawWidth: 1.6 })}
          <Line type="monotone" dataKey={rk('testAvg_ring_idx')} name="Test · Ring (AOV)" stroke={PILOT_COLORS.test.a} strokeWidth={2.4} dot={false} connectNulls={false} isAnimationActive={false} />
          <Line type="monotone" dataKey={rk('testAvg_ipt_idx')} name="Test · IPT" stroke={PILOT_COLORS.test.a} strokeWidth={1.8} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
          <Line type="monotone" dataKey={rk('ctrlAvg_ring_idx')} name="Control · Ring (AOV)" stroke={PILOT_COLORS.control.a} strokeWidth={2.2} strokeOpacity={0.6} dot={false} connectNulls={false} isAnimationActive={false} />
          <Line type="monotone" dataKey={rk('ctrlAvg_ipt_idx')} name="Control · IPT" stroke={PILOT_COLORS.control.a} strokeWidth={1.6} strokeOpacity={0.6} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );

  let caption: string;
  let body: React.ReactNode;
  if (lens === 'indexed' && mode === 'store') {
    caption = 'Each metric indexed to that store’s benchmark-window average = 100% (same axis → unambiguous co-movement). Solid = Ring, dashed = IPT.';
    body = <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{stores.map(idxPanel)}</div>;
  } else if (lens === 'indexed') {
    caption = 'Both metrics indexed to each cohort’s benchmark-window average = 100% — one honest axis, no dual-axis scale games. In pilot the test cohort’s Ring + IPT climb together while control stays flat. Solid = Ring, dashed = IPT.';
    body = groupIndexed;
  } else if (mode === 'store') {
    caption = 'Per-store dual axis — solid = Ring ($, left), dashed = IPT (count, right), one hue per store. Read each panel for Ring + IPT rising together in PILOT.';
    body = <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{stores.map(absPanel)}</div>;
  } else {
    caption = 'Group dual axis — test (warm) vs control baseline (cool, faint). Solid = Ring ($, left), dashed = IPT (count, right).';
    body = groupAbsolute;
  }

  return (
    <ChartCard
      n="★"
      title="Ring (AOV) & IPT — the effectiveness read"
      sub="Do upsell attempts lift items per transaction (IPT) and, in turn, average order value (Ring)? Ring & IPT are POS-order aggregates (coverage-independent) — so controls get a real baseline line too."
      right={headerRight}
    >
      {hasData ? (
        <>
          <p className="text-xs font-semibold text-inkMuted mb-2">{caption}</p>
          {body}
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-inkMuted border-t border-softBorder pt-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-5 border-t-2" style={{ borderColor: REF_COLORS.benchmark }} />
              solid = Ring (AOV)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-5 border-t-2 border-dashed" style={{ borderColor: REF_COLORS.benchmark }} />
              dashed = IPT (items/txn)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 h-3 rounded" style={{ background: 'rgba(213,88,71,0.13)' }} />
              pilot
            </span>
            <span className="ml-auto italic">POS aggregates — controls included; gap Jul 2–5 is a real break; tail fills as pilot days accrue.</span>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center text-center py-16 bg-cream-dark/40 rounded-2xl">
          <p className="font-display font-semibold text-ink">No Ring/IPT data yet</p>
          <p className="text-sm text-inkMuted mt-1 max-w-md">Ring (AOV) and IPT come from POS order data. Once orders are imported for the pilot window, this effectiveness read will populate.</p>
        </div>
      )}
    </ChartCard>
  );
};

// ── Revenue & Transactions — the store-level read (feat-179) ──
//
// The zoom-OUT companion to the Ring/IPT card above it. Ring & IPT answer "what
// happens WITHIN a transaction"; this answers "is the store as a whole different."
// Same four stores, same window, same visual language, different question — which
// is why this block is a deliberate near-exact clone of RingIptChart and NOT a
// new visual dialect.
//
// Revenue and Transactions are Ring's OWN numerator and denominator
// (`SUM(total_money)` and `COUNT(*)` over the same `pos_order_current` predicate,
// ADR 0001), so `Revenue ÷ Transactions === Ring` on the card above, every day.
// That identity is the correctness spine of this card — if the two cards ever
// disagree, one of them is lying.
//
// ⚠ NO LENS TOGGLE — locked spec Decision 1, Preston: "We don't need to
// normalize." Absolute $/count only; there is deliberately no `RiLens`-style
// state here and no `% of benchmark` / `% of baseline` view, not even behind a
// flag. Each panel free-scales its own y-axis (`domain=['auto','auto']`), which
// is what keeps per-store SHAPE comparable without normalizing the units.
//
// ⚠ Recharts dual-axis gotcha (the silent one): the phase-band ReferenceArea /
// ReferenceLine AND every trend <Line> must carry a yAxisId. Omit it and the
// series binds to the wrong axis — it still renders, it just lies. Hence
// phaseBands(..., 'rev') and the explicit yAxisId on every fitLines() call.
//
// ⚠ No-order days are GAPS, never zeros (spec AC7): the backend sends `null`,
// `reshapeToWide` null-pads, and `connectNulls={false}` breaks the line. Jul 2–5
// must break here exactly as it does on Ring/IPT. A $0 point would read as a
// store that did no business, which is a different — and false — claim.

/** Compact axis money, e.g. 2929.5 → `$2.9k`; small panels can't fit `$2,929.50`. */
const moneyTick = (v: number): string =>
  Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`;

// Per-metric tooltip formatter: Revenue → full `$1,234.56` (the tooltip is where
// the exact number lives, since the axis is compacted), Transactions → integer
// count. Keyed off the dataKey suffix like `riFmt`.
const rtFmt = (v: number, p?: any): string => {
  const dk = String(p?.dataKey ?? '');
  if (dk.includes('_rev'))
    return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return String(Math.round(v));
};

const RT_GROUP_KEYS = ['testAvg_rev', 'testAvg_txn', 'ctrlAvg_rev', 'ctrlAvg_txn'] as const;

/** ADR 0001, verbatim — the label follows the math, not the other way round. */
const RT_FOOTNOTE =
  "Revenue is the sum of each order's total collected — what the customer paid, including tax, tip, and service charges, after discounts. It is not tax-excluded net sales, and refunds are not subtracted. Transactions counts distinct current, non-canceled orders, dated by when the order opened in the store's local time. Revenue ÷ Transactions is exactly the Ring (AOV) above.";

const RevenueTransactionsChart: React.FC<ChartProps> = ({
  resp,
  stores,
  today,
  mode,
  rollingOn,
  trend,
  setSeries,
  setBlockTrend,
}) => {
  const hasData = React.useMemo(
    () => resp.series.some((r) => r.revenue != null || r.transactions != null),
    [resp],
  );

  const base = React.useMemo(() => reshapeToWide(resp), [resp]);
  const allKeys = React.useMemo(
    () => [...stores.flatMap((s) => [s.location_id + '_rev', s.location_id + '_txn']), ...RT_GROUP_KEYS],
    [stores],
  );

  // feat-162 trend gating, same shape as the Ring/IPT block: global TREND →
  // this block's gate → the per-metric checkbox. No lens dimension here.
  const blockTrend = trend.on && trend.blocks.revenueTransactions;
  const wantRev = blockTrend && trend.series.revenue;
  const wantTxn = blockTrend && trend.series.storeTransactions;

  const fitTargets = React.useMemo(
    () => allKeys.filter((k) => (k.endsWith('_rev') ? wantRev : wantTxn)),
    [allKeys, wantRev, wantTxn],
  );

  const { rows: data, fittedKeys } = React.useMemo(() => {
    const rolled = rollingOn ? withRolling(base, allKeys) : base;
    // Fit the RAW daily series, never the 7-day-smoothed one — the trend is over
    // observations, independent of the SMOOTHING control (feat-162).
    return withFit(rolled, fitTargets, trend.style);
  }, [base, allKeys, rollingOn, fitTargets, trend.style]);

  const rk = (key: string): string => (rollingOn ? `${key}__roll` : key);
  const sk = (key: string, name: string, color: string): SeriesKey[] => [{ key, name, color }];

  const headerRight = (
    <div className="flex items-center gap-3 flex-wrap justify-end">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-coral bg-coral-light rounded-full px-3 py-1">
        ★ STORE-LEVEL
      </span>
      {/* feat-162 per-block toggles. NOTE: this is a block gate, NOT a lens —
          there is exactly one view of this card and the label says "Absolute"
          only to name the units ($ / count), never to imply a second lens. */}
      <div className="inline-flex items-center gap-3 flex-wrap">
        <TrendCheck
          label="Trend in Absolute"
          on={trend.blocks.revenueTransactions}
          disabled={!trend.on}
          onChange={(v) => setBlockTrend('revenueTransactions', v)}
        />
        <TrendCheck
          label="Revenue"
          on={trend.series.revenue}
          disabled={!blockTrend}
          onChange={(v) => setSeries('revenue', v)}
        />
        <TrendCheck
          label="Transactions"
          on={trend.series.storeTransactions}
          disabled={!blockTrend}
          onChange={(v) => setSeries('storeTransactions', v)}
        />
      </div>
    </div>
  );

  const panelHeader = (s: StoreWithColor) => (
    <div className="flex items-center gap-2 mb-1">
      <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color }} />
      <span className="font-semibold text-sm truncate">{s.name}</span>
      <span className="text-[10px] text-inkMuted flex-shrink-0">({s.group})</span>
    </div>
  );

  // ── per-store small multiples (2×2) ──
  const storePanel = (s: StoreWithColor) => (
    <div key={s.location_id} className="bg-white rounded-2xl border border-softBorder p-3">
      {panelHeader(s)}
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            {phaseBands(resp.phases, today, false, 'rev')}
            <XAxis {...commonX} />
            <YAxis yAxisId="rev" width={44} tick={{ fontSize: 9 }} tickFormatter={moneyTick} domain={['auto', 'auto']} />
            <YAxis yAxisId="txn" orientation="right" width={30} tick={{ fontSize: 9 }} domain={['auto', 'auto']} />
            <Tooltip content={makeTooltip(resp.phases, rtFmt)} />
            {/* ⚠ dual axis — Revenue trend on 'rev' ($), Transactions trend on
                'txn' (count). Painted BEFORE the raw lines so raw stays the hero. */}
            {fitLines(sk(`${s.location_id}_rev`, `${s.name} · Revenue`, s.color), fittedKeys, trend.style, { yAxisId: 'rev', rawWidth: 2.4 })}
            {fitLines(sk(`${s.location_id}_txn`, `${s.name} · Transactions`, s.color), fittedKeys, trend.style, { yAxisId: 'txn', rawWidth: 1.8 })}
            <Line yAxisId="rev" type="monotone" dataKey={rk(`${s.location_id}_rev`)} name={`${s.name} · Revenue`} stroke={s.color} strokeWidth={2.4} dot={false} connectNulls={false} isAnimationActive={false} />
            <Line yAxisId="txn" type="monotone" dataKey={rk(`${s.location_id}_txn`)} name={`${s.name} · Transactions`} stroke={s.color} strokeWidth={1.8} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  // ── group view (single wide chart) ──
  const groupChart = (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          {phaseBands(resp.phases, today, true, 'rev')}
          <XAxis {...commonX} />
          <YAxis yAxisId="rev" width={52} tick={{ fontSize: 11 }} tickFormatter={moneyTick} domain={['auto', 'auto']} label={{ value: 'Revenue', angle: -90, position: 'insideLeft', fontSize: 10, fill: REF_COLORS.benchmark, dy: 26 }} />
          <YAxis yAxisId="txn" orientation="right" width={44} tick={{ fontSize: 11 }} domain={['auto', 'auto']} label={{ value: 'Transactions', angle: 90, position: 'insideRight', fontSize: 10, fill: REF_COLORS.benchmark, dy: 34 }} />
          <Tooltip content={makeTooltip(resp.phases, rtFmt)} />
          {/* ⚠ dual axis — every trend carries its metric's yAxisId. Control
              trends self-dim via the hue check inside fitLines. */}
          {fitLines(sk('testAvg_rev', 'Test · Revenue', PILOT_COLORS.test.a), fittedKeys, trend.style, { yAxisId: 'rev', rawWidth: 2.6 })}
          {fitLines(sk('ctrlAvg_rev', 'Control · Revenue (baseline)', PILOT_COLORS.control.a), fittedKeys, trend.style, { yAxisId: 'rev', rawWidth: 2 })}
          {fitLines(sk('testAvg_txn', 'Test · Transactions', PILOT_COLORS.test.a), fittedKeys, trend.style, { yAxisId: 'txn', rawWidth: 1.8 })}
          {fitLines(sk('ctrlAvg_txn', 'Control · Transactions (baseline)', PILOT_COLORS.control.a), fittedKeys, trend.style, { yAxisId: 'txn', rawWidth: 1.5 })}
          <Line yAxisId="rev" type="monotone" dataKey={rk('testAvg_rev')} name="Test · Revenue" stroke={PILOT_COLORS.test.a} strokeWidth={2.6} dot={false} connectNulls={false} isAnimationActive={false} />
          <Line yAxisId="txn" type="monotone" dataKey={rk('testAvg_txn')} name="Test · Transactions" stroke={PILOT_COLORS.test.a} strokeWidth={1.8} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
          <Line yAxisId="rev" type="monotone" dataKey={rk('ctrlAvg_rev')} name="Control · Revenue (baseline)" stroke={PILOT_COLORS.control.a} strokeWidth={2} strokeOpacity={0.55} dot={false} connectNulls={false} isAnimationActive={false} />
          <Line yAxisId="txn" type="monotone" dataKey={rk('ctrlAvg_txn')} name="Control · Transactions (baseline)" stroke={PILOT_COLORS.control.a} strokeWidth={1.5} strokeOpacity={0.55} strokeDasharray="5 3" dot={false} connectNulls={false} isAnimationActive={false} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );

  const caption =
    mode === 'store'
      ? 'Per-store dual axis — solid = Revenue ($, left), dashed = Transactions (count, right), one hue per store. Each panel free-scales its own axes, so read SHAPE across panels, not height.'
      : 'Group dual axis — test (warm) vs control baseline (cool, faint). Solid = Revenue ($, left), dashed = Transactions (count, right).';

  return (
    <ChartCard
      n="★"
      title="Revenue & Transactions — the store-level read"
      sub="Ring & IPT ask what happens within a transaction. This asks whether the store as a whole is different: total money through the till, and how many orders it took. POS aggregates — controls get a real baseline line too."
      right={headerRight}
    >
      {hasData ? (
        <>
          <p className="text-xs font-semibold text-inkMuted mb-2">{caption}</p>
          {mode === 'store' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{stores.map(storePanel)}</div>
          ) : (
            groupChart
          )}
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-inkMuted border-t border-softBorder pt-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-5 border-t-2" style={{ borderColor: REF_COLORS.benchmark }} />
              solid = Revenue ($)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-5 border-t-2 border-dashed" style={{ borderColor: REF_COLORS.benchmark }} />
              dashed = Transactions (count)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 h-3 rounded" style={{ background: 'rgba(213,88,71,0.13)' }} />
              pilot
            </span>
            <span className="ml-auto italic">Days with no orders are a real break in the line, never a $0 point.</span>
          </div>
          {/* AC12 — ADR 0001 verbatim. The money figure is total COLLECTED, not
              net sales; saying "net sales" here would be a false statement about
              the number, so the label follows the math. */}
          <p className="mt-2 text-[11px] leading-relaxed text-inkMuted">{RT_FOOTNOTE}</p>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center text-center py-16 bg-cream-dark/40 rounded-2xl">
          <p className="font-display font-semibold text-ink">No revenue data yet</p>
          <p className="text-sm text-inkMuted mt-1 max-w-md">
            Revenue and Transactions come from POS order data. Once orders are imported for the pilot window, this
            store-level read will populate.
          </p>
        </div>
      )}
    </ChartCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// feat-229 — Ring (AOV) & IPT, THE LONG VIEW (pre-period → pilot)
//
// A SECOND, additive card that sits directly ABOVE the existing Ring/IPT card
// (spec AC14). Same two metrics, same four per-store panels, seven months instead
// of seven weeks: 2026-01-20 → 2026-08-14, one point per Mon–Fri week.
//
// **The existing card is untouched** — its title, its Jun 22 window, its daily
// points, its rolling toggle, its Absolute default, and its per-panel
// `domain={['auto','auto']}` all stay exactly as they were (spec AC13).
//
// Five things here are deliberate and look like bugs if you don't know why:
//
//  1. **31 slots, 30 buckets.** One null spacer row keyed `2026-07-02` occupies
//     the gap so the gap `ReferenceArea` has a category to anchor to. Without it
//     Recharts renders a zero-width band and a visually continuous join — no
//     error, no warning. See `weeklyBuckets` (spec AC6, DESIGN-NOTES §4.1).
//  2. **The y-domain is SHARED across the four panels**, not `['auto','auto']`.
//     Per-panel auto makes a control store's $0.30 seven-month drift render
//     identically to a test store's $1.55 lift — a chart that disproves its own
//     thesis. See `sharedDomain` (spec AC11b, DESIGN-NOTES §2.3).
//  3. **TWO trend fits, not three** (Preston 2026-08-08, superseding the original
//     per-phase design): one over the 22 pre buckets, one over the 8
//     benchmark+pilot buckets COMBINED. No benchmark-only fit.
//  4. **The combined fit renders CONTINUOUSLY across the Jul 2–5 gap** while the
//     raw Ring/IPT lines still BREAK there. That asymmetry is the point: the fit
//     is ONE model over 8 buckets, none of which lie in the gap, and breaking it
//     would falsely imply two fits. Do not "fix" it (spec AC10).
//  5. **Band opacity differs from the existing card's** (benchmark 0.15 vs 0.07,
//     pilot 0.09 vs 0.05, same hexes). Opacity is a function of band WIDTH — a
//     rule in TOKENS.md §1m, not a divergence. See `LONG_VIEW_BAND_OPACITY`.
//
// It fetches its OWN `?start=2026-01-20` payload rather than sharing the page's,
// so a failure of the extended window is CARD-LOCAL and the rest of the page —
// including the existing Ring/IPT card underneath — keeps rendering
// (DESIGN-NOTES §8).
// ═══════════════════════════════════════════════════════════════════════════

const LONG_VIEW_TITLE = 'Ring (AOV) & IPT — the long view (pre-period → pilot)';

/**
 * Ink weight carries the emphasis, so no pixel has to move horizontally
 * (DESIGN-NOTES §2.2a). The pre-period is 71% of the axis and stays 71% of the
 * axis — compressing it would inflate its apparent slope against the pilot's,
 * which is a lie in the exact dimension this card exists to communicate. Instead
 * the pre-period is drawn as the *shadow* of history: present, measurable,
 * hoverable, not shouting.
 */
const LV_INK = {
  ring: { pre: { width: 1.6, opacity: 0.45 }, full: { width: 2.6, opacity: 1 } },
  ipt: { pre: { width: 1.1, opacity: 0.3 }, full: { width: 1.8, opacity: 1 } },
} as const;

/** Fitted trends are drawn in inkMuted — `REF_COLORS` already means "reads as a
 *  baseline, not a series", and **a fitted trend is a reference, not data**. A
 *  third hue would have read as a third series (DESIGN-NOTES §5). */
const LV_FIT = { stroke: REF_COLORS.benchmark, width: 1.5, opacity: 0.9 } as const;

type LvLens = 'absolute' | 'indexed';

const lvRingFmt = (v: number): string => `$${v.toFixed(2)}`;
const lvPctFmt = (v: number): string => `${v.toFixed(1)}%`;

/**
 * Why the short week is short. A bare "3 open days" invites "so the data is
 * broken"; naming the cause makes a thin week read as a fact about the calendar.
 */
function shortWeekCause(rows: WeeklyRow[], i: number): string {
  const row = rows[i];
  const prev = rows[i - 1];
  const next = rows[i + 1];
  if (!prev || prev.phase !== row.phase) return 'this phase starts mid-week';
  if (!next || next.phase !== row.phase) return `the ${row.phase} window ends mid-week`;
  return 'a day in this week produced no orders';
}

/**
 * Card-local tooltip. Reads the ROW rather than the payload so the gap spacer —
 * whose every metric is null, and which Recharts therefore hands us with an empty
 * payload — can still explain itself instead of silently showing nothing.
 */
function lvTooltip(rows: WeeklyRow[], store: StoreWithColor, lens: LvLens) {
  return function LvTooltipContent({ active, label }: any) {
    if (!active) return null;
    const i = rows.findIndex((r) => r.key === label);
    if (i === -1) return null;
    const row = rows[i];

    if (row.spacer) {
      return (
        <div className="rounded-xl bg-ink text-white text-xs shadow-xl px-3 py-2 max-w-xs">
          <div className="font-bold mb-1">
            {row.label} <span className="text-white/50 font-normal">· gap</span>
          </div>
          <div className="text-white/70 leading-snug">
            No bucket. Jul 2–3 open days are deliberately excluded; this is a real break, not
            missing data.
          </div>
        </div>
      );
    }

    const id = store.location_id;
    const ring = row[lens === 'indexed' ? LV.ringPct(id) : LV.ring(id)];
    const ipt = row[lens === 'indexed' ? LV.iptPct(id) : LV.ipt(id)];
    const fmt = lens === 'indexed' ? lvPctFmt : null;
    const open = row[LV.open(id)];
    const short = typeof open === 'number' && open < 5;

    return (
      <div className="rounded-xl bg-ink text-white text-xs shadow-xl px-3 py-2 max-w-xs">
        <div className="font-bold mb-1">
          {row.label} <span className="text-white/50 font-normal">· {row.phase}</span>
        </div>
        {typeof ring === 'number' && (
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: store.color }} />
            <span className="text-white/70">Ring (AOV)</span>
            <span className="ml-auto font-semibold tabular-nums">
              {fmt ? fmt(ring) : lvRingFmt(ring)}
            </span>
          </div>
        )}
        {typeof ipt === 'number' && (
          <div className="flex items-center gap-2">
            <span
              className="inline-block w-2 h-2 rounded-full border"
              style={{ borderColor: store.color }}
            />
            <span className="text-white/70">IPT</span>
            <span className="ml-auto font-semibold tabular-nums">
              {fmt ? fmt(ipt) : ipt.toFixed(2)}
            </span>
          </div>
        )}
        {typeof open === 'number' && (
          <div
            className={`mt-1 pt-1 border-t border-white/15 ${short ? 'text-warning-solid' : 'text-white/60'}`}
          >
            {open} open day{open === 1 ? '' : 's'}
            {short ? ` — ${shortWeekCause(rows, i)}.` : ' · Mon–Fri'}
            {short && (
              <span className="text-white/50">
                {' '}
                Order-weighted like any other week — not scaled to a 5-day equivalent.
              </span>
            )}
          </div>
        )}
      </div>
    );
  };
}

/**
 * Hollow marker on the Ring line wherever a bucket has fewer than 5 open days.
 * The spec requires only the tooltip (AC9); the design lead added this because a
 * tooltip is invisible until hovered and the short weeks sit at the two most
 * load-bearing places on the chart — the very first bucket and the last benchmark
 * one.
 */
function lvShortWeekDot(id: string, color: string) {
  return function LvDot(props: any) {
    const { cx, cy, payload, index } = props;
    const open = payload?.[LV.open(id)];
    if (cx == null || cy == null || payload?.spacer || typeof open !== 'number' || open === 0 || open >= 5) {
      return <g key={`lvdot-${id}-${index}`} />;
    }
    return (
      <circle
        key={`lvdot-${id}-${index}`}
        cx={cx}
        cy={cy}
        r={2.6}
        fill="#ffffff"
        stroke={color}
        strokeWidth={1.4}
      />
    );
  };
}

/** Card-local phase bands + the hard pilot boundary + the today rule. */
function lvBands(rows: WeeklyRow[], yAxisId?: string): React.ReactNode[] {
  const y = yAxisId ? { yAxisId } : {};
  const paint: Record<
    string,
    { fill: string; op: number; label?: string; labelFill: string; labelOpacity: number }
  > = {
    // PRE-PERIOD and PILOT are labelled in EVERY panel — unlike the existing
    // card's per-store panels, which pass showLabels=false. There PILOT was 70%
    // of the width and self-evident; here it is 19% and needs naming.
    // BENCHMARK is deliberately UNLABELLED: 2 of 31 slots is ~19px and a clipped
    // "BENCH…" is worse than nothing. It is named in the legend and every tooltip.
    //
    // `labelOpacity` is deliberately NOT shared — see the note at the label prop.
    pre: { fill: BAND.pre, op: LONG_VIEW_BAND_OPACITY.pre, label: 'PRE-PERIOD', labelFill: '#717182', labelOpacity: 0.75 },
    benchmark: { fill: BAND.benchmark, op: LONG_VIEW_BAND_OPACITY.benchmark, labelFill: '#B4801F', labelOpacity: 1 },
    gap: { fill: BAND.gap, op: LONG_VIEW_BAND_OPACITY.gap, labelFill: '#717182', labelOpacity: 1 },
    pilot: { fill: BAND.pilot, op: LONG_VIEW_BAND_OPACITY.pilot, label: 'PILOT', labelFill: '#B23F30', labelOpacity: 1 },
  };

  const out: React.ReactNode[] = weeklyPhaseBands(rows).map((b) => {
    const f = paint[b.phase] ?? paint.gap;
    return (
      <ReferenceArea
        key={`lvb-${b.phase}-${b.x1}`}
        {...y}
        x1={b.x1}
        x2={b.x2}
        fill={f.fill}
        fillOpacity={f.op}
        label={
          f.label
            ? {
                value: f.label,
                position: 'insideTop',
                fill: f.labelFill,
                fontSize: 8.5,
                fontWeight: 700,
                // ⚠ PER-BAND, not shared. The asymmetry is the whole point:
                // PRE-PERIOD recedes (0.75) because its band is 71% of the axis
                // and is meant to read as background; PILOT is full strength
                // because its band is 19% and has to hold the eye. Applying one
                // opacity to both dims the label that must attract to match the
                // one that must disappear — which is the emphasis inverted.
                fillOpacity: f.labelOpacity,
              }
            : undefined
        }
      />
    );
  });

  // A hard, nameable pilot boundary — the tint change alone is too soft at 19%.
  const firstPilot = rows.find((r) => r.phase === 'pilot' && !r.spacer);
  if (firstPilot) {
    out.push(
      <ReferenceLine
        key="lvb-pilot-edge"
        {...y}
        x={firstPilot.key}
        stroke={BAND.pilot}
        strokeWidth={1.1}
        strokeOpacity={0.55}
      />,
    );
  }
  return out;
}

const LongViewRingIptChart: React.FC<{ globalTrendOn: boolean }> = ({ globalTrendOn }) => {
  // Card-local view state. Deliberately NOT in `TrendPrefs`/localStorage: those
  // prefs belong to the daily cards, and a new top-level key there would change
  // what the existing prefs blob is asserted to contain.
  //
  // **Default lens = Absolute** (Preston 2026-08-09, superseding the 2026-08-08
  // "% of benchmark" default). Both cards now mount on the same lens, matching the
  // 2026-07-10 ruling for the existing card. The index remains one click away.
  //
  // Superseded reasoning, kept so the change is legible: the indexed lens strips
  // each store's level offset, so over seven months all four panels share one axis
  // and "five flat months, then a step" reads as the same shape everywhere. That is
  // still true — it is just no longer the default view.
  //
  // NOTE: Absolute is the DUAL-AXIS lens, so its ReferenceAreas MUST carry a
  // yAxisId; the indexed lens has a single default axis and must pass none. Both
  // are pinned by band-geometry tests over both lenses — see the describe.each in
  // feat229LongViewRecharts.test.tsx. Changing this default does not change which
  // lens needs which, and neither failure throws: an unresolved yAxisId renders
  // zero bands silently, in both directions.
  const [lens, setLens] = React.useState<LvLens>('absolute');
  const [showRing, setShowRing] = React.useState(true);
  const [showIpt, setShowIpt] = React.useState(true);
  const [trendOn, setTrendOn] = React.useState(false);

  const [resp, setResp] = React.useState<PilotTrendsResponse | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');

  React.useEffect(() => {
    let alive = true;
    // `Promise.resolve().then(...)` rather than calling straight through: this
    // card must degrade to its own error state for ANY failure of the extended
    // window — including a synchronous throw or a non-promise return from the
    // client — instead of taking an exception up through the effect and
    // white-screening an admin page whose other five cards are fine.
    Promise.resolve()
      .then(() => getPilotTrends(undefined, PRE_PERIOD_START))
      .then((r) => {
        if (!alive) return;
        if (!r || !Array.isArray(r.stores) || !Array.isArray(r.series)) {
          throw new Error('malformed pilot-trends payload');
        }
        setResp(r);
        setState('ready');
      })
      .catch(() => {
        // Card-local. The long view is additive, so its failure must NOT take the
        // page — or the card below it — down (DESIGN-NOTES §8).
        if (alive) setState('error');
      });
    return () => {
      alive = false;
    };
  }, []);

  const stores = React.useMemo(() => (resp ? assignStoreColors(resp.stores) : []), [resp]);
  const ids = React.useMemo(() => stores.map((s) => s.location_id), [stores]);

  const metricKey = React.useCallback(
    (id: string, metric: 'ring' | 'ipt') =>
      lens === 'indexed'
        ? metric === 'ring'
          ? LV.ringPct(id)
          : LV.iptPct(id)
        : metric === 'ring'
          ? LV.ring(id)
          : LV.ipt(id),
    [lens],
  );

  // Fits render in the Absolute lens only — the checkbox is literally "Trend in
  // Absolute" and is inert in the indexed lens, same as the existing card.
  const fitsWanted = globalTrendOn && trendOn && lens === 'absolute' && showRing;

  const { rows, fits, ringDomain, iptDomain } = React.useMemo<{
    rows: WeeklyRow[];
    fits: WeeklyFits;
    ringDomain: SharedDomain | null;
    iptDomain: SharedDomain | null;
  }>(() => {
    if (!resp) {
      return { rows: [], fits: {}, ringDomain: null, iptDomain: null };
    }
    const buckets = weeklyBuckets(resp, resp.stores);
    const ringKeys = ids.map((id) => metricKey(id, 'ring'));
    const iptKeys = ids.map((id) => metricKey(id, 'ipt'));

    const fitted: { rows: WeeklyRow[]; fits: WeeklyFits } = fitsWanted
      ? withWeeklyFits(buckets, ids, (id) => LV.ring(id))
      : { rows: buckets, fits: {} };

    return {
      rows: withPhaseSegments(fitted.rows, [...ringKeys, ...iptKeys]),
      fits: fitted.fits,
      // ONE domain per metric across all four stores — the whole point of §2.3.
      ringDomain:
        lens === 'indexed'
          ? sharedIndexedDomain(buckets, [...ringKeys, ...iptKeys])
          : sharedDomain(buckets, ringKeys),
      iptDomain: lens === 'indexed' ? null : sharedDomain(buckets, iptKeys),
    };
  }, [resp, ids, metricKey, fitsWanted, lens]);

  const hasData = React.useMemo(
    () => !!resp && resp.series.some((r) => r.ring != null || r.ipt != null),
    [resp],
  );

  /** Stores whose history starts after the window does — DESIGN-NOTES §6 state 7. */
  const shortHistory = React.useMemo(() => {
    if (!resp || !rows.length) return [] as StoreWithColor[];
    const firstBucket = rows.find((r) => !r.spacer);
    if (!firstBucket) return [];
    return stores.filter((s) => {
      const v = firstBucket[LV.ring(s.location_id)];
      return typeof v !== 'number';
    });
  }, [resp, rows, stores]);

  const domainFellBack = !!ringDomain?.fallback || !!iptDomain?.fallback;

  const headerRight = (
    <div className="flex items-center gap-3 flex-wrap justify-end">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-coral bg-coral-light rounded-full px-3 py-1">
        ★ LONG VIEW
      </span>
      <div className="inline-flex items-center gap-1.5">
        <span className="text-xs font-bold uppercase tracking-wide text-inkMuted">Lens</span>
        {/* ⚠ `· weekly` is a DELIBERATE deviation from the mockup, which shows the
            existing card's exact lens labels. The mockup renders this card in
            isolation; on the real page the two toggle groups sit ~600px apart,
            look identical, and drive different cards — a genuine usability
            problem, not only a test one. The suffix names the thing that actually
            differs (this card's points are weeks) and keeps
            `getByText('% of benchmark')` in the existing suites unambiguous
            (spec AC13). An `sr-only` qualifier does NOT work here: testing-
            library's `getByText` reads only a node's DIRECT text children, so a
            hidden child span is invisible to it while the collision remains. */}
        <div className="inline-flex rounded-full border border-softBorder bg-white p-0.5">
          <button
            onClick={() => setLens('absolute')}
            className={`rounded-full px-3 py-1 text-[13px] font-bold ${lens === 'absolute' ? 'bg-coral text-white' : 'text-inkMuted'}`}
          >
            Absolute ($ / count) · weekly
          </button>
          <button
            onClick={() => setLens('indexed')}
            className={`rounded-full px-3 py-1 text-[13px] font-bold ${lens === 'indexed' ? 'bg-coral text-white' : 'text-inkMuted'}`}
          >
            % of benchmark · weekly
          </button>
        </div>
      </div>
    </div>
  );

  const lvCheck = (
    label: React.ReactNode,
    on: boolean,
    onChange: (v: boolean) => void,
    opts: { disabled?: boolean; note?: string } = {},
  ) => (
    // The hint sits OUTSIDE the <label> on purpose: inside, it becomes part of
    // the control's accessible name ("Trend in Absolute (2 fits: …)"), which is
    // both worse to hear and a moving target every time the hint copy changes.
    <span className="inline-flex items-center gap-1.5">
      <label
        className={`flex items-center gap-1.5 text-xs font-semibold select-none ${
          opts.disabled ? 'text-inkMuted/40 cursor-not-allowed' : on ? 'text-ink cursor-pointer' : 'text-inkMuted cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          checked={on && !opts.disabled}
          disabled={opts.disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="accent-coral"
        />
        {label}
      </label>
      {opts.note && <span className="text-xs font-normal text-inkMuted/70">{opts.note}</span>}
    </span>
  );

  const toggleRow = (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3">
      {lvCheck('Ring (AOV)', showRing, setShowRing)}
      {/* `IPT` alone is the existing card's trend checkbox label; the sr-only
          qualifier disambiguates for assistive tech and for the page's tests. */}
      {lvCheck(
        <>
          IPT<span className="sr-only"> (long view)</span>
        </>,
        showIpt,
        setShowIpt,
      )}
      {lvCheck(
        <>
          Trend in Absolute<span className="sr-only"> (long view)</span>
        </>,
        trendOn,
        setTrendOn,
        {
          disabled: !globalTrendOn || lens === 'indexed',
          // named by SPAN here too — see the naming rule on `fitLinesFor`
          note: lens === 'indexed' ? '(absolute lens only)' : '(2 fits: pre-period · pilot)',
        },
      )}
      {/* No 7-day rolling toggle — and deliberately not a DISABLED one. Weekly
          buckets are already smoothed; a rolling mean over weekly points would be
          a second smoothing with no defensible window. The note is here so the
          absence doesn't read as an oversight. */}
      <span className="ml-auto text-[11px] text-inkMuted italic">
        No 7-day rolling toggle here — weekly buckets are already smoothed.
      </span>
    </div>
  );

  const panelHeader = (s: StoreWithColor) => (
    <div className="flex items-center gap-2 mb-1">
      <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color }} />
      <span className="font-semibold text-sm truncate">{s.name}</span>
      <span className="text-[10px] text-inkMuted flex-shrink-0">({s.group})</span>
    </div>
  );

  /** One metric's three phase-split <Line>s. Pre is recessive; bench+pilot full. */
  const segmentLines = (
    s: StoreWithColor,
    metric: 'ring' | 'ipt',
    yAxisId?: string,
  ): React.ReactNode[] => {
    const key = metricKey(s.location_id, metric);
    const ink = LV_INK[metric];
    const y = yAxisId ? { yAxisId } : {};
    const dash = metric === 'ipt' ? { strokeDasharray: '5 3' } : {};
    const dot = metric === 'ring' ? lvShortWeekDot(s.location_id, s.color) : false;
    return (['pre', 'bench', 'pilot'] as const).map((seg) => {
      const weight = seg === 'pre' ? ink.pre : ink.full;
      return (
        <Line
          key={`${key}-${seg}`}
          {...y}
          {...dash}
          type="monotone"
          dataKey={SEG[seg](key)}
          name={`${s.name} · ${metric === 'ring' ? 'Ring (AOV)' : 'IPT'}`}
          stroke={s.color}
          strokeWidth={weight.width}
          strokeOpacity={weight.opacity}
          dot={dot}
          activeDot={{ r: 3 }}
          // The RAW lines must BREAK across Jul 2–5 (spec AC10). The spacer row
          // carries nulls, so `connectNulls={false}` produces that break for free.
          connectNulls={false}
          isAnimationActive={false}
        />
      );
    });
  };

  /** The two Ring fits. Drawn ABOVE the raw lines — a reference reads over data. */
  const fitLinesFor = (s: StoreWithColor, yAxisId?: string): React.ReactNode[] => {
    const f = fits[s.location_id];
    if (!f) return [];
    const y = yAxisId ? { yAxisId } : {};
    return (['pre', 'pilotFit'] as const)
      .map((which) => {
        const fit = f[which];
        if (!fit) return null;
        // ⚠ NAMING RULE — required copy, not a suggestion (spec, DESIGN-NOTES
        // §5.0). Fit B is NEVER "the pilot trend". It spans the benchmark window
        // too, so its slope is partly set by pre-intervention buckets: if Ring
        // steps up at Jul 6 and then plateaus, this fit reports a positive SLOPE
        // for what is really a LEVEL SHIFT inside the fitted window, and the shape
        // gets misdescribed. Both fits are named by SPAN.
        //
        // (The mockup's `< 3 buckets → dashed at 45%` de-emphasis is deliberately
        // NOT ported: under the two-fit ruling no fit has fewer than 8 buckets, so
        // it is dead code — §5.0 says delete it rather than carry it.)
        return (
          <Line
            key={fit.column}
            {...y}
            type="linear"
            dataKey={fit.column}
            name={`${s.name} · fitted trend — ${which === 'pre' ? 'pre-period' : 'pilot'}`}
            stroke={LV_FIT.stroke}
            strokeWidth={LV_FIT.width}
            strokeOpacity={LV_FIT.opacity}
            dot={false}
            activeDot={false}
            // ⚠ The benchmark+pilot fit spans the Jul 2–5 spacer, whose value is
            // null. `connectNulls` is what draws it as ONE continuous line across
            // the gap — deliberate, and the opposite of the raw lines above
            // (spec AC10). It is a single model over 8 buckets, none in the gap.
            connectNulls
            isAnimationActive={false}
          />
        );
      })
      .filter(Boolean) as React.ReactNode[];
  };

  const domainProp = (d: SharedDomain | null) => (d ? d.domain : (['auto', 'auto'] as const));

  const absPanel = (s: StoreWithColor) => (
    <div key={s.location_id} className="bg-white rounded-2xl border border-softBorder p-3">
      {panelHeader(s)}
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            {/* ⚠ dual axis — every ReferenceArea/ReferenceLine MUST carry a
                yAxisId here. MEASURED, 2026-08-08: omitting it does NOT throw,
                contrary to the older comment on the existing card — the element
                renders ZERO rects and the band silently vanishes, leaving a chart
                that still looks plausible. So this is a correctness requirement
                that no "it didn't crash" test can catch; it is pinned by the
                band-geometry assertions in feat229LongViewRecharts.test.tsx. */}
            {lvBands(rows, 'ring')}
            <XAxis dataKey="key" tickFormatter={monthTick} interval={3} tickMargin={6} minTickGap={16} tick={{ fontSize: 9 }} />
            <YAxis yAxisId="ring" width={44} tick={{ fontSize: 9 }} tickFormatter={(v) => `$${Number(v).toFixed(1)}`} domain={domainProp(ringDomain)} allowDataOverflow={false} />
            <YAxis yAxisId="ipt" orientation="right" width={32} tick={{ fontSize: 9 }} tickFormatter={(v) => Number(v).toFixed(2)} domain={domainProp(iptDomain)} allowDataOverflow={false} />
            <Tooltip content={lvTooltip(rows, s, 'absolute')} />
            {showIpt && segmentLines(s, 'ipt', 'ipt')}
            {showRing && segmentLines(s, 'ring', 'ring')}
            {showRing && fitLinesFor(s, 'ring')}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const idxPanel = (s: StoreWithColor) => (
    <div key={s.location_id} className="bg-white rounded-2xl border border-softBorder p-3">
      {panelHeader(s)}
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            {/* Single default axis here — this lens must pass NO yAxisId.
                MEASURED, 2026-08-09 (recharts as installed), because the older
                form of this comment claimed it "would throw" and nobody had
                checked: in a single-default-axis ComposedChart, a ReferenceArea
                carrying `yAxisId="ring"` renders 0 rects and a ReferenceLine
                carrying it renders 0 lines — **no throw, no warning**, exactly
                like the missing-yAxisId case twenty lines up in `absPanel`.
                Recharts does not favour loud failure in either direction here;
                both a missing and a spurious axis id just vanish. `yAxisId={0}`
                (the real default id) does render, which is what identifies this
                as an id-resolution miss rather than a prop-validation error. */}
            {lvBands(rows)}
            <XAxis dataKey="key" tickFormatter={monthTick} interval={3} tickMargin={6} minTickGap={16} tick={{ fontSize: 9 }} />
            <YAxis width={42} tick={{ fontSize: 9 }} tickFormatter={(v) => `${Math.round(Number(v))}%`} domain={domainProp(ringDomain)} />
            <ReferenceLine y={100} stroke={REF_COLORS.benchmark} strokeDasharray="5 4" />
            <Tooltip content={lvTooltip(rows, s, 'indexed')} />
            {showIpt && segmentLines(s, 'ipt')}
            {showRing && segmentLines(s, 'ring')}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  // ⚠ THE CAPTION SWAP IS MANDATORY WHEN THE FALLBACK FIRES (spec AC11d), and it
  // is checked BEFORE the lens branch on purpose. A card that has quietly dropped
  // to per-panel scaling is the original defect wearing a different hat: every
  // panel fills its own height, a flat control looks exactly like a lifting test
  // store, and the reader has not been told to stop comparing amplitudes. If the
  // scaling changes and the sentence does not, the chart lies.
  const caption = domainFellBack
    ? 'Panels are individually scaled — compare shape, not amplitude. One store’s range is far enough from the others that a shared axis would flatten all four, so the usual cross-panel amplitude comparison does NOT hold here.'
    : lens === 'indexed'
      ? 'Each metric indexed to that store’s Jun 22 – Jul 1 benchmark average = 100% — the same baseline the card below uses, so the two reconcile. One shared axis across all four panels: “five flat months, then a step” is the same shape everywhere.'
      : 'Per-store dual axis — solid = Ring ($, left), dashed = IPT (count, right). All four panels share ONE y-domain, so a flat control store renders flat instead of being stretched to look like a lift.';

  const legendRow = (
    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-inkMuted border-t border-softBorder pt-3">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-5 border-t-2" style={{ borderColor: REF_COLORS.benchmark }} />
        solid = Ring (AOV)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-5 border-t-2 border-dashed" style={{ borderColor: REF_COLORS.benchmark }} />
        dashed = IPT (items/txn)
      </span>
      {/* the pre swatch is near-white — without the hairline it disappears */}
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-4 h-3 rounded border border-softBorder" style={{ background: BAND.pre }} />
        pre-period <span className="text-inkMuted/70">Jan 20 – Jun 21</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-4 h-3 rounded" style={{ background: 'rgba(233,162,59,0.20)' }} />
        benchmark <span className="text-inkMuted/70">Jun 22 – Jul 1</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded" style={{ background: 'rgba(113,113,130,0.22)' }} />
        gap <span className="text-inkMuted/70">Jul 2–5, real break</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-4 h-3 rounded" style={{ background: 'rgba(213,88,71,0.20)' }} />
        pilot <span className="text-inkMuted/70">Jul 6 – Aug 14</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-inkMuted/60 bg-white" />
        short week (&lt;5 open days)
      </span>
      {/* ⚠ REQUIRED COPY (spec; DESIGN-NOTES §5.0). The fits are still named by
          SPAN, which since 2026-08-09 is literally true of both: pre-period, and
          pilot. Benchmark is fitted by NOTHING — its 2 buckets were too short to
          carry a trend, and folding them into the pilot fit reported part of the
          Jul 6 level shift as slope.

          The old trailing clause explained why the second fit crossed Jul 2–5.
          It no longer crosses it — pre ends Jun 21, pilot starts Jul 6 — so that
          clause was REMOVED rather than reworded. Do not reinstate it: the gap now
          carries no line of any kind, which is what the band already says. */}
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-5 border-t-2" style={{ borderColor: REF_COLORS.benchmark }} />
        grey straight = fitted trend — pre-period vs pilot
        <span className="text-inkMuted/70">
          (Ring only; the benchmark window is not fitted)
        </span>
      </span>
    </div>
  );

  let body: React.ReactNode;
  if (state === 'loading') {
    body = (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-2xl border border-softBorder p-3">
            <div className="h-3 w-28 rounded bg-cream-dark animate-pulse mb-2" />
            <div className="h-44 rounded-xl bg-cream-dark/60 animate-pulse" />
          </div>
        ))}
      </div>
    );
  } else if (state === 'error') {
    body = (
      <div className="bg-danger-surface border border-danger-line/40 rounded-2xl p-6 text-center">
        <p className="font-semibold text-danger-text">Couldn’t load the long view</p>
        <p className="text-sm text-inkMuted mt-1">
          The extended pre-period window failed to load. The Ring &amp; IPT card below is unaffected.
        </p>
      </div>
    );
  } else if (!hasData) {
    body = (
      <div className="flex flex-col items-center justify-center text-center py-16 bg-cream-dark/40 rounded-2xl">
        <p className="font-display font-semibold text-ink">No Ring/IPT data yet</p>
        <p className="text-sm text-inkMuted mt-1 max-w-md">
          Ring (AOV) and IPT come from POS order data. Once orders are imported for the pilot
          window, this long view will populate.
        </p>
      </div>
    );
  } else {
    body = (
      <>
        {shortHistory.length > 0 && (
          <div className="mb-3 rounded-2xl bg-warning-surface border border-warning-on/30 px-4 py-3 text-xs text-ink">
            <span className="font-bold">POS history is short for </span>
            {shortHistory.map((s) => s.name).join(', ')}
            <span> — their lines start where their data starts, not at Jan 20. </span>
            <span className="font-bold">
              Do not read a cross-store level comparison across the empty stretch.
            </span>
          </div>
        )}
        <p className="text-xs font-semibold text-inkMuted mb-2">{caption}</p>
        {toggleRow}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {stores.map(lens === 'indexed' ? idxPanel : absPanel)}
        </div>
        {legendRow}
      </>
    );
  }

  return (
    <ChartCard
      n="★"
      title={LONG_VIEW_TITLE}
      sub="Five months of POS history before the benchmark window — the trend the pilot is actually being measured against. Each point is one Mon–Fri week, order-weighted (Σrevenue ÷ Σtransactions), not a mean of daily means."
      right={headerRight}
    >
      {body}
    </ChartCard>
  );
};

// ── legend strip ──
const LegendKey: React.FC<{ stores: StoreWithColor[] }> = ({ stores }) => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-inkMuted">
    <span className="font-semibold text-ink">Legend:</span>
    {stores.map((s) => (
      <span key={s.location_id} className="inline-flex items-center gap-1.5">
        <span className="inline-block w-3 h-1.5 rounded-full" style={{ background: s.color }} />
        {s.name}
        <span className="text-inkMuted/70">({s.group === 'control' ? 'control · baseline, no recording' : 'test'})</span>
      </span>
    ))}
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: REF_COLORS.target }} />
      target (bogey)
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: REF_COLORS.benchmark }} />
      benchmark
    </span>
  </div>
);

// ── state panels ──
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-cream">
    <ScorecardNavigation />
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-7">{children}</div>
  </div>
);

const PilotTrends: React.FC = () => {
  const [resp, setResp] = React.useState<PilotTrendsResponse | null>(null);
  const [error, setError] = React.useState<'forbidden' | 'error' | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [reloadKey, setReloadKey] = React.useState(0);

  // NOTE: mode / rollingOn / showDots stay UNPERSISTED (product-lead: keep the
  // feat-162 diff scoped). Only the new trend prefs round-trip to localStorage.
  const [mode, setMode] = React.useState<SeriesMode>('store');
  const [rollingOn, setRollingOn] = React.useState(false);
  const [showDots, setShowDots] = React.useState(true);

  // feat-162 (Decision 10) — lazy init so a corrupt value costs one fail-soft read,
  // not a read per render. loadTrendPrefs never throws.
  const [trend, setTrend] = React.useState<TrendPrefs>(() => loadTrendPrefs());
  React.useEffect(() => {
    saveTrendPrefs(trend);
  }, [trend]);

  const setSeries = React.useCallback(
    (id: keyof TrendSeriesPrefs, on: boolean) =>
      setTrend((p) => ({ ...p, series: { ...p.series, [id]: on } })),
    [],
  );
  const setLensTrend = React.useCallback(
    (l: RiLens, on: boolean) => setTrend((p) => ({ ...p, lens: { ...p.lens, [l]: on } })),
    [],
  );
  const setBlockTrend = React.useCallback(
    (b: keyof TrendPrefs['blocks'], on: boolean) =>
      setTrend((p) => ({ ...p, blocks: { ...p.blocks, [b]: on } })),
    [],
  );

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getPilotTrends()
      .then((r) => {
        if (alive) {
          setResp(r);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof PilotTrendsForbiddenError ? 'forbidden' : 'error');
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  if (loading) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral" />
          <p className="mt-4 text-inkMuted">Loading pilot trends…</p>
        </div>
      </Shell>
    );
  }

  if (error === 'forbidden') {
    return (
      <Shell>
        <div className="bg-white border border-softBorder rounded-3xl shadow-sm p-10 max-w-lg mx-auto text-center mt-16">
          <h1 className="text-2xl font-display font-semibold text-ink mb-2">Access denied</h1>
          <p className="text-sm text-inkMuted">
            Pilot Trends is available to Bespoke administrators only. Access is enforced by the server.
          </p>
        </div>
      </Shell>
    );
  }

  if (error === 'error') {
    return (
      <Shell>
        <div className="bg-danger-surface border border-danger-line/40 rounded-3xl p-8 max-w-lg mx-auto text-center mt-16">
          <p className="font-semibold text-danger-text">Couldn't load pilot trends</p>
          <p className="text-sm text-inkMuted mt-1">Something went wrong fetching the data. Try again in a moment.</p>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-coral text-white text-sm font-bold px-4 py-1.5 hover:bg-coral/90"
          >
            Retry
          </button>
        </div>
      </Shell>
    );
  }

  if (!resp || resp.series.length === 0) {
    return (
      <Shell>
        <div className="bg-white border border-softBorder rounded-3xl shadow-sm p-10 max-w-lg mx-auto text-center mt-16">
          <h1 className="text-xl font-display font-semibold text-ink mb-2">No pilot data yet</h1>
          <p className="text-sm text-inkMuted">Once benchmark and pilot days accrue, the trends will appear here.</p>
        </div>
      </Shell>
    );
  }

  const stores = assignStoreColors(resp.stores);
  const today = resp.series.reduce((max, r) => (r.date > max ? r.date : max), resp.window.start);
  const chartProps = { resp, stores, today, mode, showDots, rollingOn, trend, setSeries, setLensTrend, setBlockTrend };

  return (
    <Shell>
      {/* header */}
      <div className="mb-1 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display font-semibold text-3xl text-ink">Pilot Trends</h1>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-ink/5 text-inkMuted uppercase tracking-wide">Admin only</span>
          </div>
          <p className="text-inkMuted">L&amp;L upsell pilot — benchmark → pilot, test vs control, over the whole program.</p>
        </div>
        <div className="text-right text-xs text-inkMuted">
          <div>
            <span className="font-semibold text-ink">Window</span> {monthTick(resp.window.start)} → {monthTick(resp.window.end)}, 2026
          </div>
          <div>
            Test: {stores.filter((s) => s.group === 'test').map((s) => s.name).join(', ')} · Control:{' '}
            {stores.filter((s) => s.group === 'control').map((s) => s.name).join(', ')}
          </div>
        </div>
      </div>

      {/* sticky control bar — pins BELOW the ScorecardNavigation (`sticky top-0
          z-50`). Per design-lead feat-151 Type-B ruling: top-24 (96px) clears the
          nav's measured 85.5px @1280px with ~10px margin for its height variance
          (welcome-row badges flex-wrap / tenant-name length), so it won't re-tuck
          at another width/tenant. z-30 stays below the nav (nav wins) but above the
          Recharts SVG; shadow-sm + bg-cream/90 make charts read as scrolling *under*
          the pinned bar. (--nav-h CSS var stays the documented future upgrade only
          if the nav routinely exceeds ~90px.) */}
      <div className="sticky top-24 z-30 bg-cream/90 backdrop-blur border border-softBorder shadow-sm rounded-2xl px-4 py-3 my-4 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-inkMuted">Grouping</span>
          <div className="inline-flex rounded-full border border-softBorder bg-white p-0.5">
            <button onClick={() => setMode('group')} className={`rounded-full px-3 py-1 text-[13px] font-bold ${mode === 'group' ? 'bg-coral text-white' : 'text-inkMuted'}`}>
              Group avg
            </button>
            <button onClick={() => setMode('store')} className={`rounded-full px-3 py-1 text-[13px] font-bold ${mode === 'store' ? 'bg-coral text-white' : 'text-inkMuted'}`}>
              All {stores.length} stores
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-inkMuted">Smoothing</span>
          <div className="inline-flex rounded-full border border-softBorder bg-white p-0.5">
            <button onClick={() => setRollingOn(true)} className={`rounded-full px-3 py-1 text-[13px] font-bold ${rollingOn ? 'bg-coral text-white' : 'text-inkMuted'}`}>
              7-day rolling
            </button>
            <button onClick={() => setRollingOn(false)} className={`rounded-full px-3 py-1 text-[13px] font-bold ${!rollingOn ? 'bg-coral text-white' : 'text-inkMuted'}`}>
              Raw daily
            </button>
          </div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-inkMuted cursor-pointer select-none ml-1">
            <input type="checkbox" checked={showDots} onChange={(e) => setShowDots(e.target.checked)} className="accent-coral" /> daily dots
          </label>
        </div>
        {/* feat-162 TREND — deliberately its OWN control, separate from SMOOTHING:
            SMOOTHING smooths the RAW daily line (7-day); TREND draws an overall
            line across the whole window. The alternate style's 14-day window is
            longer on purpose so the two never read as the same knob (Question A(i)). */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-inkMuted">Trend</span>
          <div className="inline-flex rounded-full border border-softBorder bg-white p-0.5">
            <button onClick={() => setTrend((p) => ({ ...p, on: true }))} className={`rounded-full px-3 py-1 text-[13px] font-bold ${trend.on ? 'bg-coral text-white' : 'text-inkMuted'}`}>
              On
            </button>
            <button onClick={() => setTrend((p) => ({ ...p, on: false }))} className={`rounded-full px-3 py-1 text-[13px] font-bold ${!trend.on ? 'bg-coral text-white' : 'text-inkMuted'}`}>
              Off
            </button>
          </div>
          <div className={`inline-flex rounded-full border border-softBorder bg-white p-0.5 ${trend.on ? '' : 'opacity-40'}`}>
            <button
              disabled={!trend.on}
              onClick={() => setTrend((p) => ({ ...p, style: 'linear' }))}
              className={`rounded-full px-3 py-1 text-[13px] font-bold ${trend.style === 'linear' ? 'bg-coral text-white' : 'text-inkMuted'} ${trend.on ? '' : 'cursor-not-allowed'}`}
              title="One straight least-squares line across the whole window"
            >
              Least-squares
            </button>
            <button
              disabled={!trend.on}
              onClick={() => setTrend((p) => ({ ...p, style: 'rolling14' }))}
              className={`rounded-full px-3 py-1 text-[13px] font-bold ${trend.style === 'rolling14' ? 'bg-coral text-white' : 'text-inkMuted'} ${trend.on ? '' : 'cursor-not-allowed'}`}
              title="14-day rolling mean — a long-window trend, distinct from the 7-day SMOOTHING of the raw line"
            >
              14-day mean
            </button>
          </div>
        </div>
        <div className="ml-auto">
          <LegendKey stores={stores} />
        </div>
      </div>

      {/* phase-band legend strip */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-inkMuted mb-4 px-1">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded" style={{ background: 'rgba(233,162,59,0.20)' }} />
          Benchmark (Jun 22–Jul 1)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded" style={{ background: 'rgba(113,113,130,0.22)' }} />
          gap (Jul 2–5) — real line break, never interpolated
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded" style={{ background: 'rgba(213,88,71,0.13)' }} />
          Pilot (Jul 6–Aug 14)
        </span>
      </div>

      {/* feat-229: the long view sits DIRECTLY ABOVE the existing Ring/IPT card
          (spec AC14). It is additive — it fetches its own `?start=2026-01-20`
          payload and fails card-locally, so nothing below it can be taken down by
          the extended window. */}
      <LongViewRingIptChart globalTrendOn={trend.on} />
      {/* feat-152: Ring/IPT effectiveness read is the headline — placed at the top
          above the Upsell Score chart (Preston 2026-07-10). */}
      <RingIptChart {...chartProps} />
      {/* feat-179: the store-level read sits DIRECTLY BELOW Ring/IPT (locked spec
          Decision 2 — not above, not a tab) so the page reads as a deliberate
          zoom-out: per-transaction first, whole-store second. */}
      <RevenueTransactionsChart {...chartProps} />
      <Chart1 {...chartProps} />
      <Chart2 {...chartProps} />
      <Chart3 {...chartProps} />
      <Chart4 {...chartProps} />
    </Shell>
  );
};

export default PilotTrends;
